import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card, PageHeader, Spinner, Badge } from "@/components/ui";
import MessageThread from "@/components/MessageThread";
import { HeartPulse, Calendar, Phone, MessageSquare, Pill, FileText } from "lucide-react";

export default function MemberPortal() {
  const [m, setM] = useState(null);
  useEffect(() => { api.get("/portal/member").then((r) => setM(r.data)); }, []);
  if (!m) return <Spinner label="Loading your health summary…" />;

  return (
    <div className="vc-fade-up">
      <Card className="p-6 bg-brand-950 text-white border-0 mb-6">
        <div className="flex items-center gap-4">
          <img src={m.photo_url} alt="" className="h-16 w-16 rounded-xl object-cover" />
          <div>
            <p className="text-brand-300 text-sm">Welcome back,</p>
            <h1 className="font-display text-2xl font-bold">{m.first_name} {m.last_name}</h1>
            <p className="text-brand-200 text-sm mt-1">{m.plan} · Member since {m.enrollment_date}</p>
          </div>
        </div>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="p-5 lg:col-span-2">
          <h3 className="font-display font-semibold flex items-center gap-2 mb-4"><HeartPulse className="h-4 w-4 text-brand-700" /> My Care Plan</h3>
          {m.care_plans?.length ? m.care_plans.map((cp) => (
            <div key={cp.id} className="space-y-2">
              {cp.goals.map((g, i) => (
                <div key={i} className="flex items-center justify-between rounded-md border border-border p-3">
                  <span className="text-sm">{g.goal}</span><Badge tone={g.status === "On Track" ? "Active" : "In Progress"}>{g.status}</Badge>
                </div>
              ))}
            </div>
          )) : <p className="text-sm text-muted-foreground">Your care manager is building your personalized care plan.</p>}
        </Card>

        <div className="space-y-4">
          <Card className="p-5">
            <h3 className="font-display font-semibold flex items-center gap-2 mb-3"><Pill className="h-4 w-4 text-brand-700" /> Medications</h3>
            {m.medications?.length ? m.medications.map((md, i) => <p key={i} className="text-sm py-1">{md.name} <span className="text-xs text-muted-foreground">· {md.sig}</span></p>) : <p className="text-sm text-muted-foreground">No active medications</p>}
          </Card>
          <Card className="p-5">
            <h3 className="font-display font-semibold flex items-center gap-2 mb-3"><Phone className="h-4 w-4 text-brand-700" /> My Care Team</h3>
            <p className="text-sm font-medium">{m.care_manager_name}</p>
            <p className="text-xs text-muted-foreground">Care Manager</p>
          </Card>
        </div>
      </div>

      <Card className="p-5 mt-4">
        <h3 className="font-display font-semibold flex items-center gap-2 mb-3"><MessageSquare className="h-4 w-4 text-brand-700" /> Message My Care Team</h3>
        <MessageThread memberId={m.id} height="h-72" />
      </Card>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-4">
        {[[Calendar, "Schedule Visit"], [MessageSquare, "Message Care Team"], [FileText, "View Documents"], [Phone, "24/7 Crisis Line"]].map(([Icon, label]) => (
          <Card key={label} className="p-5 text-center cursor-pointer" hover data-testid={`portal-action-${label.toLowerCase().replace(/ /g, "-")}`}>
            <Icon className="h-6 w-6 text-brand-700 mx-auto mb-2" />
            <p className="text-sm font-medium">{label}</p>
          </Card>
        ))}
      </div>
    </div>
  );
}
